import axios from "axios";

const api = axios.create({
  baseURL: "/api", // Use proxy in development
});

export const resolveAssetUrl = (assetPath) => {
  if (!assetPath) return "";

  const getDevBackendOrigin = () => {
    if (typeof window === "undefined") return "http://localhost:5000";
    const isDevFrontendPort = window.location.port === "3000";
    if (!isDevFrontendPort) return "";
    return `${window.location.protocol}//${window.location.hostname}:5000`;
  };

  const devBackendOrigin = getDevBackendOrigin();

  if (/^https?:\/\//i.test(assetPath)) {
    try {
      const parsed = new URL(assetPath);
      const isFrontendDevOrigin = parsed.port === "3000";
      if (isFrontendDevOrigin && parsed.pathname.startsWith("/uploads/")) {
        const fallbackOrigin = `${parsed.protocol}//${parsed.hostname}:5000`;
        const backendOrigin = devBackendOrigin || fallbackOrigin;
        return `${backendOrigin}${parsed.pathname}${parsed.search || ""}${parsed.hash || ""}`;
      }
    } catch (_) {
      // Fall through and return original value for non-standard URLs
    }
    return assetPath;
  }

  const normalizedPath = String(assetPath).startsWith("/")
    ? String(assetPath)
    : `/${assetPath}`;

  const explicitOrigin =
    process.env.REACT_APP_API_ORIGIN || process.env.REACT_APP_API_BASE_URL;
  if (explicitOrigin) {
    return `${explicitOrigin.replace(/\/+$/, "")}${normalizedPath}`;
  }

  if (devBackendOrigin && normalizedPath.startsWith("/uploads/")) {
    return `${devBackendOrigin}${normalizedPath}`;
  }

  return normalizedPath;
};

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const appendRepeatedParam = (params, key, value) => {
  if (Array.isArray(value)) {
    value
      .map((entry) => String(entry || "").trim())
      .filter(Boolean)
      .forEach((entry) => params.append(key, entry));
    return;
  }

  const normalized = String(value || "").trim();
  if (normalized) {
    params.append(key, normalized);
  }
};

// Example function to get user data
export const getUserData = async (userId) => {
  try {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Example function to login
export const login = async (credentials) => {
  try {
    const response = await api.post("/auth/login", credentials);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Example function to register
export const register = async (userData) => {
  try {
    const response = await api.post("/auth/register", userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Request password reset instructions
 * @param {string} email
 * @returns {Promise<Object>}
 */
export const requestPasswordReset = async (email) => {
  try {
    const response = await api.post("/auth/forgot-password/request", { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Reset password using one-time token
 * @param {Object} payload - { token, newPassword, confirmPassword }
 * @returns {Promise<Object>}
 */
export const resetPassword = async (payload) => {
  try {
    const response = await api.post("/auth/forgot-password/reset", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get currently active check-in for logged-in promoter
 * @returns {Promise<Object>}
 */
export const getActiveCheckIn = async () => {
  try {
    const response = await api.get("/checkins/active");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create promoter check-in
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export const createCheckIn = async (payload) => {
  try {
    const response = await api.post("/checkins", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Checkout promoter active check-in
 * @param {number|string} checkInId
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export const checkoutCheckIn = async (checkInId, payload = {}) => {
  try {
    const response = await api.post(`/checkins/${checkInId}/checkout`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get promoter visits eligible for sale/return entry
 * @param {Object} options
 * @param {number} options.hours
 * @returns {Promise<Object>}
 */
export const getEligibleSalesVisits = async ({ hours = 24 } = {}) => {
  try {
    const response = await api.get("/sales/eligible-visits", {
      params: { hours },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getReturnableSaleItems = async () => {
  try {
    const response = await api.get("/sales/returnable-items");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getMyCheckInHistoryById = async (sessionGroupId) => {
  try {
    const response = await api.get(`/checkins/history/${sessionGroupId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Export logged-in promoter sales to CSV
 * @param {Object} filters - { startDate, endDate, outletId, category, search, location, product }
 * @returns {Promise} CSV file blob
 */
export const exportMySalesCsv = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.outletId) params.append("outletId", filters.outletId);
    if (filters.transactionType) params.append("transactionType", filters.transactionType);
    if (Array.isArray(filters.category) && filters.category.length > 0) {
      params.append("category", filters.category.join(","));
    } else if (filters.category) {
      params.append("category", filters.category);
    }
    if (filters.search) params.append("search", filters.search);
    appendRepeatedParam(params, "location", filters.location);
    if (Array.isArray(filters.product) && filters.product.length > 0) {
      params.append("product", filters.product.join(","));
    } else if (filters.product) {
      params.append("product", filters.product);
    }
    if (filters.currency) params.append("currency", filters.currency);

    const query = params.toString();
    const response = await api.get(query ? `/sales/export?${query}` : "/sales/export", {
      responseType: "blob",
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// ADMIN API FUNCTIONS

/**
 * Get all users with optional filtering
 * @param {Object} filters - { status, search, role, limit, offset, sortBy, sortDirection }
 * @returns {Promise} User list with pagination
 */
export const getAllUsers = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);
    if (filters.role) params.append("role", filters.role);
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortDirection) params.append("sortDirection", filters.sortDirection);
    if (filters.limit || filters.limit === 0) params.append("limit", filters.limit);
    if (filters.offset || filters.offset === 0) params.append("offset", filters.offset);

    const response = await api.get(`/admin/users?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get users pending approval
 * @returns {Promise} List of pending users
 */
export const getPendingUsers = async () => {
  try {
    const response = await api.get("/admin/users/pending");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get approval statistics
 * @returns {Promise} Statistics object
 */
export const getApprovalStats = async () => {
  try {
    const response = await api.get("/admin/users/stats");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get user details by ID
 * @param {number} userId - User ID
 * @returns {Promise} User details
 */
export const getUserById = async (userId) => {
  try {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Approve a user registration
 * @param {number} userId - User ID to approve
 * @returns {Promise} Updated user object
 */
export const approveUser = async (userId) => {
  try {
    const response = await api.put(`/admin/users/${userId}/approve`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Reject a user registration
 * @param {number} userId - User ID to reject
 * @param {string} reason - Rejection reason
 * @returns {Promise} Updated user object
 */
export const rejectUser = async (userId, reason) => {
  try {
    const response = await api.put(`/admin/users/${userId}/reject`, {
      reason,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Disable a user (remove access)
 * @param {number} userId - User ID to disable
 * @returns {Promise} Updated user object
 */
export const disableUser = async (userId) => {
  try {
    const response = await api.put(`/admin/users/${userId}/disable`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Enable a user (re-grant access)
 * @param {number} userId - User ID to enable
 * @returns {Promise} Updated user object
 */
export const enableUser = async (userId) => {
  try {
    const response = await api.put(`/admin/users/${userId}/enable`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update user details
 * @param {number} userId - User ID to update
 * @param {Object} userData - User data { full_name, email, role, is_active }
 * @returns {Promise} Updated user object
 */
export const updateUser = async (userId, userData) => {
  try {
    const response = await api.put(`/admin/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all sales with filtering
 * @param {Object} filters - { userId, startDate, endDate, outletId, category, search, limit, offset }
 * @returns {Promise} Sales list with pagination and summary
 */
export const getAllSales = async (filters = {}) => {
  try {
    if (!getAllSales._inFlightRequests) {
      getAllSales._inFlightRequests = new Map();
    }

    const params = new URLSearchParams();
    if (filters.userId) params.append("userId", filters.userId);
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.outletId) params.append("outletId", filters.outletId);
    if (Array.isArray(filters.category) && filters.category.length > 0) {
      params.append("category", filters.category.join(","));
    } else if (filters.category) {
      params.append("category", filters.category);
    }
    if (filters.search) params.append("search", filters.search);
    appendRepeatedParam(params, "promoter", filters.promoter);
    appendRepeatedParam(params, "location", filters.location);
    if (Array.isArray(filters.product) && filters.product.length > 0) {
      params.append("product", filters.product.join(","));
    } else if (filters.product) {
      params.append("product", filters.product);
    }
    if (filters.currency) params.append("currency", filters.currency);
    if (typeof filters.includeSummary === "boolean") {
      params.append("includeSummary", String(filters.includeSummary));
    }
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortDirection) params.append("sortDirection", filters.sortDirection);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.offset) params.append("offset", filters.offset);

    const query = params.toString();
    const requestKey = query;

    if (getAllSales._inFlightRequests.has(requestKey)) {
      return getAllSales._inFlightRequests.get(requestKey);
    }

    const requestPromise = api
      .get(`/admin/sales?${query}`)
      .then((response) => response.data)
      .finally(() => {
        getAllSales._inFlightRequests.delete(requestKey);
      });

    getAllSales._inFlightRequests.set(requestKey, requestPromise);
    return requestPromise;
  } catch (error) {
    throw error;
  }
};

export const getAdminSaleById = async (saleId) => {
  try {
    const response = await api.get(`/admin/sales/${saleId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateAdminSaleById = async (saleId, payload = {}) => {
  try {
    const formData = new FormData();
    formData.append("items", JSON.stringify(payload.items || []));
    formData.append("notes", payload.notes || "");
    formData.append("sale_date", payload.sale_date || "");
    formData.append("check_in_id", payload.check_in_id || "");
    formData.append("reason", payload.reason || "");
    if (payload.clear_receipt_image) {
      formData.append("clear_receipt_image", "true");
    }
    if (payload.receiptFile) {
      formData.append("receipt", payload.receiptFile);
    }

    const response = await api.put(`/admin/sales/${saleId}`, formData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteAdminSaleById = async (saleId, payload = {}) => {
  try {
    const response = await api.delete(`/admin/sales/${saleId}`, {
      data: payload,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEligibleSaleReassignmentSessions = async (userId) => {
  try {
    const response = await api.get(
      `/admin/promoters/${userId}/sessions/eligible-for-sale-reassignment`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get options for sales report filters
 * @param {Object} params - { type: "promoter"|"location"|"product"|"category", q, limit, userId, startDate, endDate, outletId, search, promoter, location }
 * @returns {Promise} Options array
 */
export const getSalesFilterOptions = async ({
  type,
  q = "",
  limit = 20,
  userId,
  startDate,
  endDate,
  outletId,
  search,
  promoter,
  location,
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (type) params.append("type", type);
    if (q) params.append("q", q);
    if (limit) params.append("limit", limit);
    if (userId) params.append("userId", userId);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (outletId) params.append("outletId", outletId);
    if (search) params.append("search", search);
    appendRepeatedParam(params, "promoter", promoter);
    appendRepeatedParam(params, "location", location);

    const response = await api.get(`/admin/sales/filter-options?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get sales grouped by salesperson
 * @param {Object} filters - { startDate, endDate }
 * @returns {Promise} Sales grouped by salesperson
 */
export const getSalesBySalesperson = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);

    const response = await api.get(`/admin/sales/by-salesperson?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get sales statistics
 * @param {Object} filters - { startDate, endDate }
 * @returns {Promise} Sales statistics
 */
export const getSalesStats = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);

    const response = await api.get(`/admin/sales/stats?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get dashboard statistics
 * @param {Object} filters - { promoterId }
 * @returns {Promise} Dashboard statistics with today's sales and active sessions
 */
export const getDashboardStats = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.promoterId) params.append("promoterId", filters.promoterId);

    const query = params.toString();
    const response = await api.get(
      query ? `/admin/dashboard/stats?${query}` : "/admin/dashboard/stats",
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get promoter options for dashboard filter
 * @param {Object} params - { q, limit }
 * @returns {Promise}
 */
export const getDashboardPromoters = async ({ q = "", limit = 20 } = {}) => {
  try {
    const searchParams = new URLSearchParams();
    if (q) searchParams.append("q", q);
    if (limit) searchParams.append("limit", limit);

    const query = searchParams.toString();
    const response = await api.get(
      query ? `/admin/dashboard/promoters?${query}` : "/admin/dashboard/promoters",
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Global admin search
 * @param {Object} params - { q, scope, limit, signal }
 * @returns {Promise}
 */
export const searchAdminGlobal = async ({ q = "", scope = "all", limit = 5, signal } = {}) => {
  try {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (scope) params.append("scope", scope);
    if (limit) params.append("limit", limit);

    const query = params.toString();
    const response = await api.get(query ? `/admin/search?${query}` : "/admin/search", {
      signal,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get admin sessions history
 * @param {Object} filters - { promoterIds, promoterId, startDate, endDate, status, limit, offset }
 * @returns {Promise}
 */
export const getAdminSessions = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (Array.isArray(filters.promoterIds) && filters.promoterIds.length > 0) {
      params.append("promoterIds", filters.promoterIds.join(","));
    } else if (filters.promoterId) {
      params.append("promoterId", filters.promoterId);
    }
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.status) params.append("status", filters.status);
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortDirection) params.append("sortDirection", filters.sortDirection);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.offset || filters.offset === 0) params.append("offset", filters.offset);

    const query = params.toString();
    const response = await api.get(query ? `/admin/sessions?${query}` : "/admin/sessions");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAdminSessionById = async (checkInId) => {
  try {
    const response = await api.get(`/admin/sessions/${checkInId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateAdminSessionById = async (checkInId, payload = {}) => {
  try {
    const response = await api.put(`/admin/sessions/${checkInId}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteAdminSessionById = async (checkInId, payload = {}) => {
  try {
    const response = await api.delete(`/admin/sessions/${checkInId}`, {
      data: payload,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get promoter helper options for sessions filters
 * @param {Object} params - { q, limit }
 * @returns {Promise}
 */
export const getAdminSessionPromoters = async ({ q = "", limit = 20 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (limit) params.append("limit", limit);

    const query = params.toString();
    const response = await api.get(
      query ? `/admin/sessions/promoters?${query}` : "/admin/sessions/promoters",
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get outlet radius settings by outlet id (admin)
 * @param {number|string} outletId
 * @returns {Promise}
 */
export const getAdminOutletRadiusById = async (outletId) => {
  try {
    const response = await api.get(`/admin/outlets/${outletId}/radius`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update outlet radius settings (admin)
 * @param {number|string} outletId
 * @param {Object} payload - { allowed_radius_meters: number | null }
 * @returns {Promise}
 */
export const updateAdminOutletRadiusById = async (outletId, payload) => {
  try {
    const response = await api.patch(`/admin/outlets/${outletId}/radius`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Bulk update outlet radius settings (admin)
 * @param {Object} payload - { q?: string, postcode?: string, allowed_radius_meters: number | null }
 * @returns {Promise}
 */
export const bulkUpdateAdminOutletRadius = async (payload) => {
  try {
    const response = await api.patch("/admin/outlets/radius/bulk", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get outlets for admin radius management
 * @param {Object} params - { q, limit, offset, postcode }
 * @returns {Promise}
 */
export const getAdminOutlets = async ({ q = "", limit = 100, offset = 0, postcode = "" } = {}) => {
  try {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (limit) params.append("limit", limit);
    if (offset || offset === 0) params.append("offset", offset);
    if (postcode) params.append("postcode", postcode);

    const query = params.toString();
    const response = await api.get(query ? `/admin/outlets?${query}` : "/admin/outlets");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get admin product display proofs
 * @param {Object} filters - { promoterIds, promoterId, proofType, startDate, endDate, sortBy, sortDirection, limit, offset }
 * @returns {Promise}
 */
export const getAdminDisplayProofs = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (Array.isArray(filters.promoterIds) && filters.promoterIds.length > 0) {
      params.append("promoterIds", filters.promoterIds.join(","));
    } else if (filters.promoterId) {
      params.append("promoterId", filters.promoterId);
    }
    if (filters.proofType && filters.proofType !== "all") {
      params.append("proof_type", filters.proofType);
    }
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortDirection) params.append("sortDirection", filters.sortDirection);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.offset || filters.offset === 0) params.append("offset", filters.offset);

    const query = params.toString();
    const response = await api.get(
      query ? `/admin/display-proofs?${query}` : "/admin/display-proofs",
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get admin display proof detail by id
 * @param {number|string} proofId
 * @returns {Promise}
 */
export const getAdminDisplayProofById = async (proofId) => {
  try {
    const response = await api.get(`/admin/display-proofs/${proofId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update admin display proof by id (super admin)
 * @param {number|string} proofId
 * @param {Object} payload - { proof_type? }
 * @returns {Promise}
 */
export const updateAdminDisplayProofById = async (proofId, payload) => {
  try {
    const response = await api.put(`/admin/display-proofs/${proofId}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete admin display proof by id (super admin)
 * @param {number|string} proofId
 * @returns {Promise}
 */
export const deleteAdminDisplayProofById = async (proofId) => {
  try {
    const response = await api.delete(`/admin/display-proofs/${proofId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get promoter display proof detail by id
 * @param {number|string} proofId
 * @returns {Promise}
 */
export const getMyDisplayProofById = async (proofId) => {
  try {
    const response = await api.get(`/display-proofs/me/${proofId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get active display proof types for authenticated users
 * @returns {Promise}
 */
export const getDisplayProofTypes = async () => {
  try {
    const response = await api.get("/display-proofs/types");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get display proof types (admin)
 * @param {Object} params - { includeInactive?: boolean }
 * @returns {Promise}
 */
export const getAdminDisplayProofTypes = async ({ includeInactive = false } = {}) => {
  try {
    const query = includeInactive ? "?includeInactive=true" : "";
    const response = await api.get(`/admin/display-proof-types${query}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create display proof type (admin)
 * @param {Object} payload - { name, code?, sort_order?, is_active? }
 * @returns {Promise}
 */
export const createAdminDisplayProofType = async (payload) => {
  try {
    const response = await api.post("/admin/display-proof-types", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update display proof type (admin)
 * @param {number|string} proofTypeId
 * @param {Object} payload - { name?, sort_order?, is_active? }
 * @returns {Promise}
 */
export const updateAdminDisplayProofType = async (proofTypeId, payload) => {
  try {
    const response = await api.put(`/admin/display-proof-types/${proofTypeId}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete/archive display proof type (admin)
 * @param {number|string} proofTypeId
 * @returns {Promise}
 */
export const deleteAdminDisplayProofType = async (proofTypeId) => {
  try {
    const response = await api.delete(`/admin/display-proof-types/${proofTypeId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get active competitors for the display-proof form (authenticated users)
 * @returns {Promise}
 */
export const getDisplayProofCompetitors = async () => {
  try {
    const response = await api.get("/display-proofs/competitors");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get product groups/categories that have at least one sellable product —
 * used to tag competitor observations with a category (for fair price
 * comparisons) and to filter the admin Competitor Intelligence panel.
 * @returns {Promise}
 */
export const getProductGroups = async () => {
  try {
    const response = await api.get("/product-hierarchy/groups");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get competitors (admin)
 * @param {Object} params - { includeInactive?: boolean }
 * @returns {Promise}
 */
export const getAdminCompetitors = async ({ includeInactive = false } = {}) => {
  try {
    const query = includeInactive ? "?includeInactive=true" : "";
    const response = await api.get(`/admin/competitors${query}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create competitor (super admin)
 * @param {Object} payload - { name, code?, is_active? }
 * @returns {Promise}
 */
export const createAdminCompetitor = async (payload) => {
  try {
    const response = await api.post("/admin/competitors", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update competitor (super admin)
 * @param {number|string} competitorId
 * @param {Object} payload - { name?, is_active? }
 * @returns {Promise}
 */
export const updateAdminCompetitor = async (competitorId, payload) => {
  try {
    const response = await api.put(`/admin/competitors/${competitorId}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete/archive competitor (super admin)
 * @param {number|string} competitorId
 * @returns {Promise}
 */
export const deleteAdminCompetitor = async (competitorId) => {
  try {
    const response = await api.delete(`/admin/competitors/${competitorId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get outlets that have display proofs (for the insights outlet filter)
 * @returns {Promise}
 */
export const getDisplayProofInsightOutlets = async () => {
  try {
    const response = await api.get("/admin/display-proof-insights/outlets");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get the Outlet x Product breakdown (admin) — which own-brand products the
 * promoters selected per outlet, from the model selection (not note keywords).
 * @param {Object} filters - { startDate, endDate, outletId, promoterIds }
 * @returns {Promise}
 */
export const getDisplayProofInsightOutletProducts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.outletId) params.append("outletId", filters.outletId);
    if (filters.currency) params.append("currency", filters.currency);
    if (Array.isArray(filters.promoterIds) && filters.promoterIds.length > 0) {
      params.append("promoterIds", filters.promoterIds.join(","));
    }
    const query = params.toString();
    const response = await api.get(
      query
        ? `/admin/display-proof-insights/outlet-products?${query}`
        : "/admin/display-proof-insights/outlet-products",
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get competitor activity (admin) — competitor-tagged items grouped by
 * competitor, then the outlets where each competitor was observed.
 * @param {Object} filters - { startDate, endDate, outletId, promoterIds }
 * @returns {Promise}
 */
export const getDisplayProofInsightCompetitorActivity = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.outletId) params.append("outletId", filters.outletId);
    if (filters.currency) params.append("currency", filters.currency);
    if (Array.isArray(filters.promoterIds) && filters.promoterIds.length > 0) {
      params.append("promoterIds", filters.promoterIds.join(","));
    }
    const query = params.toString();
    const response = await api.get(
      query
        ? `/admin/display-proof-insights/competitor-activity?${query}`
        : "/admin/display-proof-insights/competitor-activity",
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get competitor intelligence (admin) — our pricing vs. each competitor's,
 * pricing trend direction, activity trend (this period vs. the preceding one),
 * and promotion frequency. startDate/endDate are required.
 * @param {Object} filters - { startDate, endDate, outletId, productGroupId, currency }
 * @returns {Promise}
 */
export const getDisplayProofInsightCompetitorIntelligence = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.outletId) params.append("outletId", filters.outletId);
    if (filters.productGroupId) params.append("productGroupId", filters.productGroupId);
    if (filters.currency) params.append("currency", filters.currency);
    const query = params.toString();
    const response = await api.get(
      query
        ? `/admin/display-proof-insights/competitor-intelligence?${query}`
        : "/admin/display-proof-insights/competitor-intelligence",
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Force checkout an active session from admin panel
 * @param {number|string} checkInId
 * @param {Object} payload - { checkout_reason?: "break" | "off_duty" }
 * @returns {Promise}
 */
export const forceAdminSessionCheckout = async (checkInId, payload = {}) => {
  try {
    const response = await api.patch(`/admin/sessions/${checkInId}/checkout`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Export admin display proofs to CSV
 * @param {Object} filters - { promoterIds, proofType, startDate, endDate }
 * @returns {Promise} CSV blob response
 */
export const exportAdminDisplayProofsCsv = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (Array.isArray(filters.promoterIds) && filters.promoterIds.length > 0) {
      params.append("promoterIds", filters.promoterIds.join(","));
    }
    if (filters.proofType && filters.proofType !== "all") {
      params.append("proof_type", filters.proofType);
    }
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);

    const query = params.toString();
    const response = await api.get(
      query ? `/admin/display-proofs/export?${query}` : "/admin/display-proofs/export",
      { responseType: "blob" },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Export admin sessions history to CSV
 * @param {Object} filters
 * @returns {Promise} CSV blob response
 */
export const exportAdminSessionsCsv = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (Array.isArray(filters.promoterIds) && filters.promoterIds.length > 0) {
      params.append("promoterIds", filters.promoterIds.join(","));
    } else if (filters.promoterId) {
      params.append("promoterId", filters.promoterId);
    }
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.status) params.append("status", filters.status);
    if (filters.limit) params.append("limit", filters.limit);

    const query = params.toString();
    const response = await api.get(
      query ? `/admin/sessions/export?${query}` : "/admin/sessions/export",
      { responseType: "blob" },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Export sales to CSV
 * @param {Object} filters - { userId, startDate, endDate, outletId, category, search }
 * @returns {Promise} CSV file blob
 */
export const exportSalesToCSV = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.userId) params.append("userId", filters.userId);
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.outletId) params.append("outletId", filters.outletId);
    if (Array.isArray(filters.category) && filters.category.length > 0) {
      params.append("category", filters.category.join(","));
    } else if (filters.category) {
      params.append("category", filters.category);
    }
    if (filters.search) params.append("search", filters.search);
    appendRepeatedParam(params, "promoter", filters.promoter);
    appendRepeatedParam(params, "location", filters.location);
    if (Array.isArray(filters.product) && filters.product.length > 0) {
      params.append("product", filters.product.join(","));
    } else if (filters.product) {
      params.append("product", filters.product);
    }
    if (filters.currency) params.append("currency", filters.currency);

    const response = await api.get(`/admin/sales/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getCurrencyRate = async (date, to = "SGD", from = "MYR") => {
  try {
    const params = new URLSearchParams({ to, from });
    if (date) params.append("date", date);
    const response = await api.get(`/currency/rate?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// STOCK REQUEST API FUNCTIONS
/**
 * Checks whether an outlet has a serving warehouse.
 *
 * Only outlets linked to a warehouse can request stock.
 * Used to control access to the Request Stock section.
 *
 * @param {number|string} outletId
 * @returns {Promise} { success, data: { has_warehouse, warehouse, outlet_id, outlet_name } }
 */

export const getOutletWarehouse = async (outletId) => {
  try {
    const response = await api.get(`/stock/outlets/${outletId}/warehouse`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Gets products stocked at the outlet's warehouse and their current stock.
 *
 * Stock quantity is for information only; promoters can request more than is available.
 *
 * @param {number|string} outletId - Outlet ID.
 * @param {Object} params - Search and pagination options: q, limit, offset, inStockOnly.
 * @returns {Promise} { success, has_warehouse, warehouse, data: [...] }
 */

export const getOutletStockAvailability = async (
  outletId,
  { q = "", limit = 30, offset = 0, inStockOnly = false } = {},
) => {
  try {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (limit) params.append("limit", limit);
    if (offset || offset === 0) params.append("offset", offset);
    if (inStockOnly) params.append("in_stock_only", "true");

    const query = params.toString();
    const response = await api.get(
      query
        ? `/stock/outlets/${outletId}/availability?${query}`
        : `/stock/outlets/${outletId}/availability`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Submits a stock request for the promoter's current outlet.
 *
 * - Quantities can exceed available stock.
 * - Validates that each product exists and is stocked at the outlet's warehouse.
 * - Rejects invalid products and identifies them in details.rejections.
 *
 * @param {Object} payload - { outlet_id, check_in_id?, items: [{ product_id, quantity }], notes? }
 * @returns {Promise} { success, data: <created request> }
 */

export const requestStock = async (payload) => {
  try {
    const response = await api.post("/stock/requests", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get the authenticated promoter's own stock requests.
 * @param {Object} filters - { status, outletId, limit, offset }
 * @returns {Promise} { success, data, pagination }
 */
export const getMyStockRequests = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.outletId) params.append("outlet_id", filters.outletId);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.offset || filters.offset === 0) params.append("offset", filters.offset);

    const query = params.toString();
    const response = await api.get(
      query ? `/stock/requests?${query}` : "/stock/requests",
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get one of the authenticated promoter's own stock requests by id.
 * @param {number|string} stockRequestId
 * @returns {Promise} { success, data }
 */
export const getMyStockRequestById = async (stockRequestId) => {
  try {
    const response = await api.get(`/stock/requests/${stockRequestId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Withdraw a pending stock request.
 * @param {number|string} stockRequestId
 * @returns {Promise}
 */
export const cancelMyStockRequest = async (stockRequestId) => {
  try {
    const response = await api.put(`/stock/requests/${stockRequestId}/cancel`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ADMIN STOCK REQUEST API FUNCTIONS

/**
 * Get stock requests for review (admin).
 * @param {Object} filters - { status, outletId, warehouseId, limit, offset }
 * @returns {Promise} { success, data, pagination }
 */
export const getAdminStockRequests = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.outletId) params.append("outlet_id", filters.outletId);
    if (filters.warehouseId) params.append("warehouse_id", filters.warehouseId);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.offset || filters.offset === 0) params.append("offset", filters.offset);

    const query = params.toString();
    const response = await api.get(
      query ? `/admin/stock-requests?${query}` : "/admin/stock-requests",
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get stock request counts per status (admin) — for queue badges.
 * @returns {Promise}
 */
export const getAdminStockRequestStats = async () => {
  try {
    const response = await api.get("/admin/stock-requests/stats");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Exports stock requests to CSV, with one row per product line.
 * Date filters are optional and inclusive.
 * Returns the raw Axios response, including the CSV Blob and filename headers.
 *
 * @param {Object} filters - { startDate, endDate, status, outletId, warehouseId }
 * @returns {Promise} Axios response containing a Blob.
 */

/**
 * Gets stock-request notification recipients.
 *
 * @returns {Promise} { success, data: { admins: string[], cc: string[] } }
 */

export const getAdminStockNotificationRecipients = async () => {
  try {
    const response = await api.get("/admin/stock-request-notifications");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Replace stock-request notification recipients (super-admin).
 * @param {Object} payload - { admins: string[], cc: string[] }
 * @returns {Promise}
 */
export const setAdminStockNotificationRecipients = async (payload) => {
  try {
    const response = await api.put(
      "/admin/stock-request-notifications",
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const exportAdminStockRequestsCsv = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.status) params.append("status", filters.status);
    if (filters.outletId) params.append("outlet_id", filters.outletId);
    if (filters.warehouseId) params.append("warehouse_id", filters.warehouseId);

    const query = params.toString();
    const response = await api.get(
      query
        ? `/admin/stock-requests/export?${query}`
        : "/admin/stock-requests/export",
      { responseType: "blob" },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Gets a stock request's details and current warehouse stock.
 *
 * Stock levels are informational only; approval is not blocked by availability.
 *
 * @param {number|string} stockRequestId - Stock request ID.
 * @returns {Promise} { success, data, availability }
 */

export const getAdminStockRequestById = async (stockRequestId) => {
  try {
    const response = await api.get(`/admin/stock-requests/${stockRequestId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Approve a pending stock request (admin).
 * @param {number|string} stockRequestId
 * @param {Object} payload - { review_notes? }
 * @returns {Promise}
 */
export const approveAdminStockRequest = async (stockRequestId, payload = {}) => {
  try {
    const response = await api.put(
      `/admin/stock-requests/${stockRequestId}/approve`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Reject a pending stock request (admin).
 * @param {number|string} stockRequestId
 * @param {Object} payload - { review_notes? }
 * @returns {Promise}
 */
export const rejectAdminStockRequest = async (stockRequestId, payload = {}) => {
  try {
    const response = await api.put(
      `/admin/stock-requests/${stockRequestId}/reject`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export { api };
export default api;
